import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { MonoLabel } from "../components/MonoLabel";
import { ToastStack, useToasts } from "../components/Toast";
import {
  MAX_SESSION_MS,
  SILENCE_TIMEOUT_MS,
  ZassoSessionManager,
  type AutoTerminationReason,
  type ZassoSegment,
  type ZassoState,
  type ZassoStopResult,
} from "../channels/zasso/ZassoSessionManager";
import {
  buildMarkdownForDownload,
  buildMarkdownForVault,
  formatDuration,
  nextZassoPathPreview,
  type ZassoTurn,
} from "../channels/zasso/format";
import {
  readLastSession,
  writeLastSession,
  type ZassoLastSession,
} from "../channels/zasso/lastSession";
import {
  type Settings,
  type VaultSaveResponse,
  type ZassoVaultMode,
} from "../lib/api-types";
import { applySinkId } from "../lib/devices";
import type { SelectedDevices } from "../lib/deviceStorage";

const TICK_MS = 1000;

/** UI-level phase on top of the session-manager state. */
type UiMode = "idle" | "starting" | "active" | "stopping" | "stopped";

/**
 * Koe Zasso tab — hands-free thought-coach conversation.
 * spec §1.4, §2.3.3, §2.4.3, §2.10.
 */
export function ZassoTab({
  settings,
  devices,
}: {
  settings: Settings;
  devices?: SelectedDevices;
}) {
  // ── Session state mirrored from the manager ──────────────
  const [state, setState] = useState<ZassoState>("idle");
  const [turns, setTurns] = useState<ZassoSegment[]>([]);
  const [currentUserDelta, setCurrentUserDelta] = useState("");
  const [currentAssistantDelta, setCurrentAssistantDelta] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [mode, setMode] = useState<UiMode>("idle");
  const [stopResult, setStopResult] = useState<ZassoStopResult | null>(null);
  /** Name of the tool currently executing (search_notes / read_note / null). */
  const [activeTool, setActiveTool] = useState<string | null>(null);

  // ── Ticking info ─────────────────────────────────────────
  const [elapsedSec, setElapsedSec] = useState(0);
  const [nextPath, setNextPath] = useState(() => nextZassoPathPreview());

  // ── Persisted last-session footer ────────────────────────
  const [lastSession, setLastSession] = useState<ZassoLastSession | null>(
    () => readLastSession(),
  );

  const managerRef = useRef<ZassoSessionManager | null>(null);
  const sessionStartRef = useRef<Date | null>(null);
  const audioElRef = useRef<HTMLAudioElement | null>(null);
  const conversationBoxRef = useRef<HTMLDivElement | null>(null);

  const { toasts, push: pushToast, dismiss: dismissToast } = useToasts();

  // ── 1Hz tick ─────────────────────────────────────────────
  useEffect(() => {
    const tick = () => {
      setNextPath(nextZassoPathPreview());
      if (mode === "active" && sessionStartRef.current) {
        setElapsedSec(
          Math.floor(
            (Date.now() - sessionStartRef.current.getTime()) / 1000,
          ),
        );
      }
    };
    const id = window.setInterval(tick, TICK_MS);
    return () => window.clearInterval(id);
  }, [mode]);

  // ── Auto-scroll conversation box ─────────────────────────
  useEffect(() => {
    const box = conversationBoxRef.current;
    if (box) box.scrollTop = box.scrollHeight;
  }, [turns, currentUserDelta, currentAssistantDelta]);

  // ── Tear down manager on unmount ─────────────────────────
  useEffect(() => {
    return () => {
      managerRef.current?.stop().catch(() => {
        /* ignore during teardown */
      });
      managerRef.current = null;
    };
  }, []);

  // ── Save helpers ─────────────────────────────────────────
  const persistLastSession = useCallback(
    (savedPath: string, durationSec: number, turnCount: number) => {
      const entry: ZassoLastSession = {
        savedPath,
        durationSec,
        turnCount,
        savedAt: Date.now(),
      };
      writeLastSession(entry);
      setLastSession(entry);
    },
    [],
  );

  const saveToVault = useCallback(
    async (
      finalTurns: readonly ZassoTurn[],
      startedAt: Date,
      durationSec: number,
      assistantTurnCount: number,
    ): Promise<VaultSaveResponse> => {
      const { markdown, meta } = buildMarkdownForVault(finalTurns, startedAt);
      const res = await fetch("/api/vault/save", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          source: "Zasso",
          title: meta.title,
          content: markdown,
          dateTimeIso: meta.dateTimeIso,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `vault save failed: ${res.status} ${text.slice(0, 200)}`,
        );
      }
      const saved = (await res.json()) as VaultSaveResponse;
      persistLastSession(saved.savedPath, durationSec, assistantTurnCount);
      return saved;
    },
    [persistLastSession],
  );

  const runAutoSave = useCallback(
    async (result: ZassoStopResult, startedAt: Date) => {
      try {
        const saved = await saveToVault(
          result.turns,
          startedAt,
          result.durationSec,
          result.assistantTurnCount,
        );
        const body = result.hadPendingDelta
          ? `保存しました · 末尾は未確定のまま · ${saved.savedPath}`
          : `Vaultに保存しました · ${saved.savedPath}`;
        pushToast(body, {
          durationMs: 5000,
          ...(saved.vaultUrl
            ? { link: { href: saved.vaultUrl, label: "OPEN →" } }
            : {}),
        });
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        pushToast(`ERR · VAULT_FAIL · ${msg}`, {
          tone: "error",
          durationMs: 5000,
        });
      }
    },
    [pushToast, saveToVault],
  );

  // ── Handlers ─────────────────────────────────────────────
  const handleStart = useCallback(async () => {
    if (managerRef.current) return;
    setErrorMessage(null);
    setStopResult(null);
    setTurns([]);
    setCurrentUserDelta("");
    setCurrentAssistantDelta("");
    setElapsedSec(0);
    setMode("starting");

    const manager = new ZassoSessionManager({
      onStateChange: setState,
      onConversationChange: () => {
        const m = managerRef.current;
        if (!m) return;
        setTurns([...m.getSegments()]);
        setCurrentUserDelta(m.getCurrentUserDelta());
        setCurrentAssistantDelta(m.getCurrentAssistantDelta());
      },
      onRemoteTrack: (stream) => {
        if (audioElRef.current) {
          audioElRef.current.srcObject = stream;
          // Phase 3: apply user's output device choice (no-op on iOS Safari).
          void applySinkId(audioElRef.current, devices?.outputId ?? null);
          void audioElRef.current.play().catch(() => {
            pushToast("tap to enable audio output", "warn");
          });
        }
      },
      onSessionStarted: () => {
        sessionStartRef.current = new Date();
        setMode("active");
      },
      onAutoTerminated: (reason: AutoTerminationReason) => {
        const label =
          reason === "max_session"
            ? "max session duration reached"
            : "auto-disconnected after silence";
        pushToast(`Auto-disconnected · ${label}`, {
          tone: "warn",
          durationMs: 5000,
        });
      },
      onToolStarted: (name) => setActiveTool(name),
      onToolFinished: (_name) => setActiveTool(null),
      onError: (m) => {
        setErrorMessage(m);
        pushToast(`ERR · ${m}`, "error");
      },
    });
    managerRef.current = manager;

    try {
      await manager.start(settings.zassoVoice, devices?.inputId ?? null);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      if (
        msg.toLowerCase().includes("permission") ||
        msg.toLowerCase().includes("notallowed")
      ) {
        pushToast("ERR · NO_MIC · 0x07 — enable mic access", "error");
      } else {
        pushToast(`ERR · ${msg}`, "error");
      }
      setErrorMessage(msg);
      setMode("idle");
      managerRef.current = null;
    }
  }, [devices?.inputId, devices?.outputId, pushToast, settings.zassoVoice]);

  const handleStop = useCallback(async () => {
    const manager = managerRef.current;
    if (!manager) return;
    setMode("stopping");
    let result: ZassoStopResult;
    try {
      result = await manager.stop();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setErrorMessage(msg);
      pushToast(`ERR · ${msg}`, "error");
      setMode("idle");
      managerRef.current = null;
      return;
    }
    setTurns([...manager.getSegments()]);
    setCurrentUserDelta("");
    setCurrentAssistantDelta("");
    setStopResult(result);
    managerRef.current = null;

    const startedAt = sessionStartRef.current ?? new Date();
    const policy: ZassoVaultMode = settings.zassoVaultMode;
    if (policy === "auto-save" && result.turns.length > 0) {
      setMode("stopped");
      await runAutoSave(result, startedAt);
      setMode("idle");
    } else {
      setMode("stopped");
    }
  }, [pushToast, runAutoSave, settings.zassoVaultMode]);

  const handleCopy = useCallback(async () => {
    if (!stopResult) return;
    const startedAt = sessionStartRef.current ?? new Date();
    const { markdown } = buildMarkdownForVault(stopResult.turns, startedAt);
    try {
      await navigator.clipboard.writeText(markdown);
      pushToast("copied to clipboard");
    } catch {
      pushToast("ERR · copy failed", "error");
    }
  }, [pushToast, stopResult]);

  const handleDownload = useCallback(() => {
    if (!stopResult) return;
    const startedAt = sessionStartRef.current ?? new Date();
    const { markdown, meta } = buildMarkdownForDownload(
      stopResult.turns,
      stopResult.durationSec,
      startedAt,
    );
    const blob = new Blob([markdown], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `zasso-${meta.stamp}.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    pushToast(`downloaded · zasso-${meta.stamp}.md`);
  }, [pushToast, stopResult]);

  const handleManualVaultSave = useCallback(async () => {
    if (!stopResult) return;
    const startedAt = sessionStartRef.current ?? new Date();
    try {
      const saved = await saveToVault(
        stopResult.turns,
        startedAt,
        stopResult.durationSec,
        stopResult.assistantTurnCount,
      );
      const body = stopResult.hadPendingDelta
        ? `保存しました · 末尾は未確定のまま · ${saved.savedPath}`
        : `Vaultに保存しました · ${saved.savedPath}`;
      pushToast(body, {
        durationMs: 5000,
        ...(saved.vaultUrl
          ? { link: { href: saved.vaultUrl, label: "OPEN →" } }
          : {}),
      });
      setMode("idle");
      setStopResult(null);
      setTurns([]);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      pushToast(`ERR · VAULT_FAIL · ${msg}`, {
        tone: "error",
        durationMs: 5000,
      });
    }
  }, [pushToast, saveToVault, stopResult]);

  const handleDismissResult = useCallback(() => {
    setMode("idle");
    setStopResult(null);
    setTurns([]);
    setCurrentUserDelta("");
    setCurrentAssistantDelta("");
  }, []);

  // ── Derived ──────────────────────────────────────────────
  const elapsedLabel = useMemo(() => {
    const m = Math.floor(elapsedSec / 60);
    const s = elapsedSec % 60;
    return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }, [elapsedSec]);

  const nearMaxSession = elapsedSec >= MAX_SESSION_MS / 1000 - 60;

  // ── Render ───────────────────────────────────────────────
  return (
    <section className="flex min-h-full flex-col gap-5 px-5 py-6">
      <ToastStack toasts={toasts} onDismiss={dismissToast} />

      {/* Hidden audio sink for the remote assistant voice. */}
      <audio ref={audioElRef} autoPlay playsInline hidden />

      <div className="space-y-1">
        <div className="font-mono text-[10px] tracking-[0.22em] text-subtext">
          ZAS · {modeLabel(mode, state, activeTool)}
        </div>
        <h1 className="koe-hand text-4xl text-ink">
          {mode === "active" ? "on channel." : "channel idle."}
        </h1>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <MonoLabel label="VOICE">{settings.zassoVoice}</MonoLabel>
        <MonoLabel label="POLICY">{settings.zassoVaultMode}</MonoLabel>
        <MonoLabel label="MAX_SESSION">
          {formatDuration(MAX_SESSION_MS / 1000)}
        </MonoLabel>
        <MonoLabel label="SILENCE">
          {formatDuration(SILENCE_TIMEOUT_MS / 1000)}
        </MonoLabel>
      </div>

      {errorMessage && (
        <div
          role="alert"
          className="rounded-box border border-rust bg-cream-soft px-3 py-2 font-mono text-[11px] text-ink"
        >
          <span className="text-rust">ERR · </span>
          {errorMessage}
        </div>
      )}

      <div className="koe-rule-dotted" />

      {/* Idle — coach teaser + last/next */}
      {mode === "idle" && (
        <>
          <div className="space-y-1 font-mono text-[11px] leading-relaxed text-subtext">
            <div className="text-ink">COACH · 思考整理</div>
            <div>4つのレンズで深掘り</div>
            <div>動機 / 前提 / 反証 / 次の一歩</div>
          </div>

          <div className="space-y-2 font-mono text-[11px] text-subtext">
            <div>
              <span className="text-ink/70">LAST · </span>
              {lastSession ? (
                <span>
                  {lastSession.savedPath}{" "}
                  <span className="text-rust">✓</span>{" "}
                  {formatDuration(lastSession.durationSec)} ·{" "}
                  {lastSession.turnCount} turns
                </span>
              ) : (
                <span className="italic opacity-70">no session yet</span>
              )}
            </div>
            <div>
              <span className="text-ink/70">NEXT · </span>
              {nextPath}
            </div>
          </div>
        </>
      )}

      {/* Active / stopping — conversation + elapsed */}
      {(mode === "active" || mode === "stopping") && (
        <>
          <div className="flex items-baseline justify-between">
            <span className="font-mono text-[10px] tracking-[0.22em] text-subtext">
              ELAPSED
            </span>
            <span
              className={[
                "font-mono text-3xl tabular-nums tracking-tight",
                nearMaxSession ? "text-rust" : "text-ink",
              ].join(" ")}
            >
              {elapsedLabel}
            </span>
          </div>

          <div
            ref={conversationBoxRef}
            className="min-h-[220px] flex-1 overflow-y-auto rounded-box border border-ink/15 bg-cream-soft p-3 font-mono text-[13px] leading-relaxed text-ink"
          >
            <ConversationBody
              turns={turns}
              currentUserDelta={currentUserDelta}
              currentAssistantDelta={currentAssistantDelta}
              state={state}
            />
          </div>

          {mode === "stopping" && (
            <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
              stopping · settling conversation…
            </p>
          )}
        </>
      )}

      {/* Stopped — action panel */}
      {mode === "stopped" && stopResult && (
        <div className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between font-mono text-[11px] text-subtext">
            <span>
              {formatDuration(stopResult.durationSec)} ·{" "}
              {stopResult.assistantTurnCount} turns
            </span>
            {stopResult.autoTerminationReason && (
              <span className="text-rust">
                {stopResult.autoTerminationReason === "max_session"
                  ? "auto: max session"
                  : "auto: silence"}
              </span>
            )}
          </div>
          <div className="max-h-[40vh] overflow-y-auto rounded-box border border-ink/15 bg-cream-soft p-3 font-mono text-[13px] leading-relaxed text-ink">
            {stopResult.turns.length > 0 ? (
              <ConversationBody
                turns={stopResult.turns}
                currentUserDelta=""
                currentAssistantDelta=""
                state="closed"
              />
            ) : (
              <span className="italic opacity-60">(空)</span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <ActionButton onClick={handleCopy}>COPY</ActionButton>
            <ActionButton onClick={handleDownload}>DL · .md</ActionButton>
            {settings.zassoVaultMode !== "never" && (
              <ActionButton onClick={handleManualVaultSave}>
                VAULT
              </ActionButton>
            )}
          </div>
          <button
            type="button"
            onClick={handleDismissResult}
            className="self-end font-mono text-[10px] tracking-[0.2em] text-subtext hover:text-rust"
          >
            ✕ DISCARD &amp; BACK
          </button>
        </div>
      )}

      {/* START / END button */}
      <div className="flex flex-1 flex-col items-center justify-end gap-3 pt-4 pb-2">
        {(mode === "idle" || mode === "stopped") && (
          <button
            type="button"
            onClick={handleStart}
            disabled={mode === "stopped"}
            aria-label="Open channel"
            className="flex h-40 w-40 select-none items-center justify-center rounded-full bg-rust font-mono text-[11px] tracking-[0.22em] text-cream shadow-md transition-transform active:scale-[0.98] hover:bg-rust/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            OPEN · CHANNEL
          </button>
        )}
        {mode === "starting" && (
          <button
            type="button"
            disabled
            className="flex h-40 w-40 items-center justify-center rounded-full bg-rust/40 font-mono text-[11px] tracking-[0.22em] text-cream"
          >
            LINK…
          </button>
        )}
        {(mode === "active" || mode === "stopping") && (
          <button
            type="button"
            onClick={handleStop}
            disabled={mode === "stopping"}
            aria-label="End conversation"
            className="flex h-40 w-40 items-center justify-center rounded-full bg-ink font-mono text-[11px] tracking-[0.22em] text-cream shadow-md transition-transform active:scale-[0.98] disabled:opacity-60"
          >
            {mode === "stopping" ? "CLOSING…" : "END · CHANNEL"}
          </button>
        )}
        <p className="font-mono text-[10px] tracking-[0.18em] text-subtext">
          {mode === "idle" && "tap, then just talk · VAD auto-turns"}
          {mode === "starting" && "opening channel…"}
          {mode === "active" && activeHint(state, activeTool)}
          {mode === "stopping" && "finalising conversation"}
          {mode === "stopped" && "review · save · or discard"}
        </p>
      </div>
    </section>
  );
}

function ActionButton({
  onClick,
  children,
}: {
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="rounded-box border border-ink/30 px-3 py-2 font-mono text-[11px] tracking-[0.2em] text-ink hover:bg-ink hover:text-cream"
    >
      {children}
    </button>
  );
}

function ConversationBody({
  turns,
  currentUserDelta,
  currentAssistantDelta,
  state,
}: {
  turns: ZassoSegment[];
  currentUserDelta: string;
  currentAssistantDelta: string;
  state: ZassoState;
}) {
  if (
    turns.length === 0 &&
    !currentUserDelta &&
    !currentAssistantDelta
  ) {
    return (
      <span className="text-subtext italic">
        listening · start speaking when ready
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {turns.map((t, i) => (
        <TurnBubble key={`${t.completedAt}-${i}`} turn={t} pending={false} />
      ))}
      {currentUserDelta && (
        <TurnBubble
          turn={{
            role: "user",
            text: currentUserDelta,
            completedAt: Date.now(),
          }}
          pending
        />
      )}
      {currentAssistantDelta && (
        <TurnBubble
          turn={{
            role: "assistant",
            text: currentAssistantDelta,
            completedAt: Date.now(),
          }}
          pending
          state={state}
        />
      )}
    </div>
  );
}

function TurnBubble({
  turn,
  pending,
  state,
}: {
  turn: ZassoSegment;
  pending: boolean;
  state?: ZassoState;
}) {
  const isUser = turn.role === "user";
  const prefix = isUser ? "USER" : "COACH";
  const prefixColour = isUser ? "text-ink/70" : "text-rust";
  const textColour = pending ? "opacity-60" : "";
  return (
    <div className={["whitespace-pre-wrap", textColour].join(" ")}>
      <span className={`font-mono text-[10px] tracking-wider ${prefixColour}`}>
        {prefix} ·{" "}
      </span>
      {turn.text}
      {pending && !isUser && state === "speaking" && (
        <span className="ml-1 text-subtext">▌</span>
      )}
    </div>
  );
}

function modeLabel(
  mode: UiMode,
  state: ZassoState,
  activeTool: string | null,
): string {
  if (mode === "starting") return "LINK…";
  if (mode === "stopping") return "STOPPING";
  if (mode === "stopped") return "DONE";
  if (mode === "idle") return state === "error" ? "FAULT" : "STBY";
  // active — tool calls take precedence over the fine-grained VAD state
  // so the user sees why the coach has paused.
  if (activeTool === "search_notes") return "SEARCH";
  if (activeTool === "read_note") return "READ";
  switch (state) {
    case "listening":
      return "LISTEN";
    case "thinking":
      return "THINK";
    case "speaking":
      return "SPEAK";
    default:
      return "ACTIVE";
  }
}

function activeHint(state: ZassoState, activeTool: string | null): string {
  if (activeTool === "search_notes") return "searching vault…";
  if (activeTool === "read_note") return "reading note…";
  switch (state) {
    case "listening":
      return "listening · speak freely";
    case "thinking":
      return "thinking…";
    case "speaking":
      return "coach is speaking · interrupt anytime";
    default:
      return "active";
  }
}
